import { Box } from '@mui/material'
import { PropsWithChildren } from 'react'

import { useAuthUser } from '../store/useAuthUser'
import { FeatureFlag, featureFlags } from '../types/featureFlag'
import { UserFeature } from '../types/user'

type UseFeatureFlag = {
  FeatureContainer: ({ children }: PropsWithChildren) => JSX.Element
  isFeatureEnabled: boolean
}

/**
 * @example
 * ```tsx
 *   const { FeatureContainer, isFeatureEnabled } = useFeatureFlag('foo')
 *
 *   const someOuterCondition = isFeatureEnabled ? 'bar' : 'baz'
 *
 *   return <FeatureContainer> <SomeComponentHiddenBehindFlag /> </FeatureContainer>
 * ```
 */
export const useFeatureFlag = (featureName: UserFeature['name']): UseFeatureFlag => {
  const {
    user: { features },
  } = useAuthUser()

  const providedFlag: FeatureFlag | undefined = featureFlags.find(
    (flag: FeatureFlag): boolean => flag === featureName,
  )

  if (providedFlag === undefined) {
    throw Error(`Feature flag with provided name "${featureName}" does not exists.`)
  }

  const feature: undefined | UserFeature = features.find(
    (feature: UserFeature): boolean => feature.name === featureName,
  )

  if (feature === undefined) {
    return {
      FeatureContainer: (): JSX.Element => <></>,
      isFeatureEnabled: false,
    }
  }

  const { enabled } = feature

  return {
    FeatureContainer: ({ children }: PropsWithChildren): JSX.Element =>
      enabled ? <Box id={`feature-${featureName}`}>{children}</Box> : <></>,
    isFeatureEnabled: enabled,
  }
}
